import React, { useEffect, useState, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import LoadingSpinner from './LoadingSpinner';
import { 
  ROUTES, 
  STORAGE_KEYS, 
  AIRLINE_DISPLAY_NAMES, 
  DISCLAIMER_TEXT,
  LABELS,
  BUTTON_LABELS,
  ERROR_MESSAGES,
  SUCCESS_MESSAGES
} from '../constants/appConstants';
import '../styles/Disclaimer.scss';

interface UserAuthProfile {
  email: string;
  emplid: string;
  legacyid: string;
  company: string;
  locale: string;
  name: string;
  given_name: string;
  family_name: string;
}

interface DisclaimerProps {
  onAccept: () => void;
}

const Disclaimer: React.FC<DisclaimerProps> = ({ onAccept }) => {
  const navigate = useNavigate();
  const [userData, setUserData] = useState<UserAuthProfile | null>(null);
  const [loading, setLoading] = useState<boolean>(true);  
  const navigationInProgress = useRef(false);
  useEffect(() => {
    const loadUserData = () => {
      let storedDataString: string | null = null;

      // Find OIDC user data in localStorage
      for (let i = 0; i < localStorage.length; i++) {
        const key = localStorage.key(i);
        if (key?.startsWith(STORAGE_KEYS.OIDC_USER_PREFIX)) {
          storedDataString = localStorage.getItem(key);
          break;
        }
      }

      if (storedDataString) {
        try {
          const storedData = JSON.parse(storedDataString);
          const profile: UserAuthProfile = {
            email: storedData.profile.email,
            emplid: storedData.profile.emplid,
            legacyid: storedData.profile.legacyid,
            company: storedData.profile.company,
            locale: storedData.profile.locale,
            name: storedData.profile.name,
            given_name: storedData.profile.given_name,
            family_name: storedData.profile.family_name,
          };
          setUserData(profile);
        } catch (error) {
          console.error(ERROR_MESSAGES.PARSE_USER_DATA_ERROR, error);
        } finally {
          setLoading(false);
        }
      } else {
        console.warn(ERROR_MESSAGES.NO_USER_DATA_WARNING);
        setLoading(false);
      }
    };

    loadUserData();
  }, []);

  const handleAccept = useCallback(() => {
    // console.log(SUCCESS_MESSAGES.DISCLAIMER_ACCEPTED);
    localStorage.setItem(STORAGE_KEYS.DISCLAIMER_ACCEPTED, 'true');
          navigationInProgress.current = true;
          navigate(ROUTES.OVERVIEW, { replace: true });
          setTimeout(() => { navigationInProgress.current = false; }, 100);
    onAccept();
    // Navigation will be handled by App.tsx after state updates
  }, [onAccept]);

  const handleCancel = useCallback(() => {
    // console.log(SUCCESS_MESSAGES.DISCLAIMER_CANCELED);
    navigate(ROUTES.ROOT, { replace: true });
  }, [navigate]);

  if (loading) {
    return <LoadingSpinner data-testid="loading-spinner" />;
  }

  if (!userData) {
    return <div className="error-message">{ERROR_MESSAGES.NO_USER_DATA}</div>;
  }

  const airlineDisplayName = AIRLINE_DISPLAY_NAMES[userData.company] || userData.company;

  return (
    <div className="acars-history">
      <header className="acars-history-header">
        <h1>{LABELS.APP_TITLE}</h1>
        <div className="acars-history-user">
          <span className="acars-history-name">
            {`${userData.given_name || ''} ${userData.family_name || ''}`.trim()}
          </span>
          <span className="acars-history-airline">
            {LABELS.AIRLINE_LABEL} {airlineDisplayName}
          </span>
        </div>
      </header>
      <div className="acars-history-content">
        <div className="acars-history-notice">
          <p className="acars-history-notice-heading">{DISCLAIMER_TEXT.HEADING}</p>
          <p className="acars-history-text">
            {DISCLAIMER_TEXT.NOTICE}
            <span>{DISCLAIMER_TEXT.PERMISSION_REQUEST}</span>
          </p>
        </div>
        <div className="acars-history-actions">
          <button 
            className="acars-history-button accept" 
            onClick={handleAccept}
            type="button"
          >
            {BUTTON_LABELS.ACCEPT}
          </button>
          <button 
            className="acars-history-button cancel" 
            onClick={handleCancel}
            type="button"
          >
            {BUTTON_LABELS.CANCEL}
          </button>
        </div>
      </div>
    </div>
  );
};

export default Disclaimer;
